/**
 * HFDS — Headzflex Design System
 * Infrastructure: Runtime + Event Bus
 * Version: 1.0.1
 *
 * Responsibilities:
 * - Register HFDS components.
 * - Initialize and destroy component instances.
 * - Prevent duplicate initialization.
 * - Support Shopify Theme Editor lifecycle events.
 * - Provide a documented event bus.
 *
 * Important:
 * HFDS Core may expose window.HFDS as a frozen object.
 * Therefore, this Runtime remains available through:
 *
 * window.HFDSRuntime
 * window.HFDSEvents
 */

(() => {
  "use strict";

  const VERSION = "1.0.1";
  const DESIGN_MODE = Boolean(
    window.Shopify?.designMode
  );

  const INITIALIZED_ATTRIBUTE =
    "data-hf-runtime-initialized";

  const COMPONENT_ATTRIBUTE =
    "data-hf-runtime-component";

  const componentRegistry = new Map();
  const eventRegistry = new Map();
  const instances = new WeakMap();

  const state = {
    initialized: false,
    initializing: false,
    initializedInstances: 0,
    destroyedInstances: 0
  };

  /**
   * Log only inside Shopify Design Mode.
   *
   * @param {"info"|"warn"|"error"} level
   * @param {string} message
   * @param {unknown} [data]
   */
  function log(level, message, data) {
    if (!DESIGN_MODE) {
      return;
    }

    const prefix = "[HFDS Runtime]";

    if (data !== undefined) {
      console[level](
        `${prefix} ${message}`,
        data
      );

      return;
    }

    console[level](
      `${prefix} ${message}`
    );
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeName(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-_]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  /**
   * Return matching elements from a context.
   * Includes the context itself when applicable.
   *
   * @param {Document|DocumentFragment|HTMLElement} context
   * @param {string} selector
   * @returns {HTMLElement[]}
   */
  function queryElements(
    context,
    selector
  ) {
    const matches = [];

    if (
      context instanceof HTMLElement &&
      context.matches(selector)
    ) {
      matches.push(context);
    }

    if (
      context instanceof Document ||
      context instanceof DocumentFragment ||
      context instanceof HTMLElement
    ) {
      context
        .querySelectorAll(selector)
        .forEach((element) => {
          if (element instanceof HTMLElement) {
            matches.push(element);
          }
        });
    }

    return [...new Set(matches)];
  }

  /**
   * @param {HTMLElement} element
   * @returns {Map<string, object>}
   */
  function getElementInstances(element) {
    let elementInstances =
      instances.get(element);

    if (!elementInstances) {
      elementInstances = new Map();

      instances.set(
        element,
        elementInstances
      );
    }

    return elementInstances;
  }

  /**
   * @param {HTMLElement} element
   * @param {Map<string, object>} elementInstances
   */
  function updateElementAttributes(
    element,
    elementInstances
  ) {
    const names = [
      ...elementInstances.keys()
    ];

    if (names.length === 0) {
      element.removeAttribute(
        INITIALIZED_ATTRIBUTE
      );

      element.removeAttribute(
        COMPONENT_ATTRIBUTE
      );

      return;
    }

    element.setAttribute(
      INITIALIZED_ATTRIBUTE,
      "true"
    );

    element.setAttribute(
      COMPONENT_ATTRIBUTE,
      names.join(" ")
    );
  }

  /**
   * Register one public HFDS event.
   *
   * @param {{
   *   name: string,
   *   owner?: string,
   *   description?: string,
   *   payload?: string[]
   * }} definition
   *
   * @returns {boolean}
   */
  function registerEvent(definition) {
    if (
      !definition ||
      typeof definition !== "object"
    ) {
      return false;
    }

    const name = String(
      definition.name || ""
    ).trim();

    if (!name.startsWith("hfds:")) {
      log(
        "warn",
        `Event "${name}" must begin with "hfds:".`
      );

      return false;
    }

    eventRegistry.set(name, {
      name,
      owner: String(
        definition.owner || "HFDS"
      ),
      description: String(
        definition.description || ""
      ),
      payload: Array.isArray(
        definition.payload
      )
        ? [...definition.payload]
        : []
    });

    return true;
  }

  /**
   * Emit an HFDS event.
   *
   * @param {string} eventName
   * @param {Record<string, unknown>} [detail]
   * @param {EventTarget} [target]
   * @returns {boolean}
   */
  function emit(
    eventName,
    detail = {},
    target = document
  ) {
    const name = String(
      eventName || ""
    ).trim();

    if (!name || !target?.dispatchEvent) {
      return false;
    }

    return target.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        cancelable: true,
        composed: true,
        detail: {
          ...detail,
          hfds: {
            runtimeVersion: VERSION,
            timestamp: Date.now()
          }
        }
      })
    );
  }

  /**
   * Subscribe to an event.
   *
   * @param {string} eventName
   * @param {EventListener} handler
   * @param {{
   *   target?: EventTarget,
   *   once?: boolean,
   *   passive?: boolean,
   *   signal?: AbortSignal
   * }} [options]
   *
   * @returns {() => void}
   */
  function on(
    eventName,
    handler,
    options = {}
  ) {
    const target =
      options.target || document;

    if (
      !eventName ||
      typeof handler !== "function" ||
      !target?.addEventListener
    ) {
      return () => {};
    }

    const listenerOptions = {
      once: Boolean(options.once),
      passive: Boolean(options.passive)
    };

    if (options.signal) {
      listenerOptions.signal =
        options.signal;
    }

    target.addEventListener(
      eventName,
      handler,
      listenerOptions
    );

    return () => {
      target.removeEventListener(
        eventName,
        handler,
        listenerOptions
      );
    };
  }

  /**
   * Resolve after one event.
   *
   * @param {string} eventName
   * @param {{
   *   target?: EventTarget,
   *   timeout?: number
   * }} [options]
   *
   * @returns {Promise<Event>}
   */
  function once(
    eventName,
    options = {}
  ) {
    const target =
      options.target || document;

    const timeout = Number(
      options.timeout || 0
    );

    return new Promise(
      (resolve, reject) => {
        let timeoutId = null;

        const handler = (event) => {
          if (timeoutId !== null) {
            window.clearTimeout(
              timeoutId
            );
          }

          resolve(event);
        };

        target.addEventListener(
          eventName,
          handler,
          {
            once: true
          }
        );

        if (timeout > 0) {
          timeoutId =
            window.setTimeout(() => {
              target.removeEventListener(
                eventName,
                handler
              );

              reject(
                new Error(
                  `HFDS event timeout: ${eventName}`
                )
              );
            }, timeout);
        }
      }
    );
  }

  /**
   * Register an HFDS component.
   *
   * @param {{
   *   name: string,
   *   selector: string,
   *   factory: Function,
   *   priority?: number
   * }} definition
   *
   * @returns {boolean}
   */
  function registerComponent(
    definition
  ) {
    if (
      !definition ||
      typeof definition !== "object"
    ) {
      log(
        "error",
        "Component registration requires an object."
      );

      return false;
    }

    const name = normalizeName(
      definition.name
    );

    const selector = String(
      definition.selector || ""
    ).trim();

    const factory =
      definition.factory;

    const priority =
      Number.isFinite(
        Number(definition.priority)
      )
        ? Number(definition.priority)
        : 100;

    if (!name) {
      log(
        "error",
        "Component requires a valid name."
      );

      return false;
    }

    if (!selector) {
      log(
        "error",
        `Component "${name}" requires a selector.`
      );

      return false;
    }

    if (typeof factory !== "function") {
      log(
        "error",
        `Component "${name}" requires a factory.`
      );

      return false;
    }

    if (componentRegistry.has(name)) {
      log(
        "warn",
        `Component "${name}" is already registered.`
      );

      return false;
    }

    componentRegistry.set(name, {
      name,
      selector,
      factory,
      priority
    });

    if (state.initialized) {
      initializeDefinition(
        componentRegistry.get(name),
        document
      );
    }

    log(
      "info",
      `Registered component "${name}".`
    );

    return true;
  }

  /**
   * @param {{
   *   name: string,
   *   selector: string,
   *   factory: Function
   * }} definition
   * @param {Document|DocumentFragment|HTMLElement} context
   */
  function initializeDefinition(
    definition,
    context
  ) {
    const elements = queryElements(
      context,
      definition.selector
    );

    elements.forEach((element) => {
      const elementInstances =
        getElementInstances(element);

      if (
        elementInstances.has(
          definition.name
        )
      ) {
        return;
      }

      try {
        const instance =
          definition.factory(
            element,
            {
              runtime:
                window.HFDSRuntime,
              events:
                window.HFDSEvents
            }
          );

        elementInstances.set(
          definition.name,
          instance &&
          typeof instance === "object"
            ? instance
            : {}
        );

        updateElementAttributes(
          element,
          elementInstances
        );

        state.initializedInstances += 1;

        emit(
          "hfds:runtime:component-ready",
          {
            component:
              definition.name,
            element
          },
          element
        );
      } catch (error) {
        log(
          "error",
          `Failed to initialize "${definition.name}".`,
          error
        );

        emit(
          "hfds:runtime:component-error",
          {
            component:
              definition.name,
            element,
            error
          },
          element
        );
      }
    });
  }

  /**
   * Initialize registered components.
   *
   * @param {Document|DocumentFragment|HTMLElement} [context]
   */
  function initialize(
    context = document
  ) {
    if (state.initializing) {
      return;
    }

    state.initializing = true;

    try {
      const definitions = [
        ...componentRegistry.values()
      ].sort(
        (first, second) =>
          first.priority -
          second.priority
      );

      definitions.forEach(
        (definition) => {
          initializeDefinition(
            definition,
            context
          );
        }
      );

      state.initialized = true;

      emit(
        "hfds:runtime:ready",
        {
          version: VERSION,
          registeredComponents:
            componentRegistry.size
        }
      );
    } finally {
      state.initializing = false;
    }
  }

  /**
   * @param {HTMLElement} element
   * @param {string} componentName
   */
  function destroyElementComponent(
    element,
    componentName
  ) {
    const elementInstances =
      instances.get(element);

    if (
      !elementInstances ||
      !elementInstances.has(
        componentName
      )
    ) {
      return;
    }

    const instance =
      elementInstances.get(
        componentName
      );

    try {
      if (
        instance &&
        typeof instance.destroy ===
          "function"
      ) {
        instance.destroy();
      }
    } catch (error) {
      log(
        "error",
        `Failed to destroy "${componentName}".`,
        error
      );
    }

    elementInstances.delete(
      componentName
    );

    updateElementAttributes(
      element,
      elementInstances
    );

    state.destroyedInstances += 1;

    emit(
      "hfds:runtime:component-destroyed",
      {
        component: componentName,
        element
      },
      element
    );
  }

  /**
   * Destroy components inside a context.
   *
   * @param {Document|DocumentFragment|HTMLElement} [context]
   */
  function destroy(
    context = document
  ) {
    componentRegistry.forEach(
      (definition) => {
        const elements = queryElements(
          context,
          definition.selector
        );

        elements.forEach((element) => {
          destroyElementComponent(
            element,
            definition.name
          );
        });
      }
    );
  }

  /**
   * @param {Document|DocumentFragment|HTMLElement} [context]
   */
  function refresh(
    context = document
  ) {
    destroy(context);
    initialize(context);

    emit(
      "hfds:runtime:refreshed",
      {
        context
      }
    );
  }

  /**
   * @param {string} componentName
   * @param {Document|DocumentFragment|HTMLElement} [context]
   * @returns {boolean}
   */
  function unregisterComponent(
    componentName,
    context = document
  ) {
    const name = normalizeName(
      componentName
    );

    const definition =
      componentRegistry.get(name);

    if (!definition) {
      return false;
    }

    queryElements(
      context,
      definition.selector
    ).forEach((element) => {
      destroyElementComponent(
        element,
        name
      );
    });

    componentRegistry.delete(name);

    return true;
  }

  /**
   * @param {HTMLElement} element
   * @param {string} componentName
   * @returns {object|null}
   */
  function getInstance(
    element,
    componentName
  ) {
    if (!(element instanceof HTMLElement)) {
      return null;
    }

    return (
      instances
        .get(element)
        ?.get(
          normalizeName(componentName)
        ) || null
    );
  }

  /**
   * @returns {object}
   */
  function getStatus() {
    return {
      version: VERSION,
      initialized:
        state.initialized,
      registeredComponents:
        componentRegistry.size,
      initializedInstances:
        state.initializedInstances,
      destroyedInstances:
        state.destroyedInstances,
      registeredEvents:
        eventRegistry.size,
      designMode: DESIGN_MODE,
      hfdsCoreAvailable:
        Boolean(window.HFDS)
    };
  }

  /**
   * @returns {object[]}
   */
  function getRegisteredComponents() {
    return [
      ...componentRegistry.values()
    ].map((definition) => ({
      name: definition.name,
      selector: definition.selector,
      priority: definition.priority
    }));
  }

  /**
   * @returns {object[]}
   */
  function getRegisteredEvents() {
    return [
      ...eventRegistry.values()
    ];
  }

  const publicEvents =
    Object.freeze({
      version: VERSION,
      register: registerEvent,
      emit,
      on,
      once,
      getRegisteredEvents
    });

  const publicRuntime =
    Object.freeze({
      version: VERSION,
      register: registerComponent,
      unregister:
        unregisterComponent,
      initialize,
      destroy,
      refresh,
      getInstance,
      getStatus,
      getRegisteredComponents
    });

  [
    {
      name:
        "hfds:runtime:ready",
      owner:
        "HFDS Runtime",
      description:
        "Runtime initialized.",
      payload: [
        "version",
        "registeredComponents"
      ]
    },
    {
      name:
        "hfds:runtime:component-ready",
      owner:
        "HFDS Runtime",
      description:
        "Component initialized.",
      payload: [
        "component",
        "element"
      ]
    },
    {
      name:
        "hfds:runtime:component-error",
      owner:
        "HFDS Runtime",
      description:
        "Component initialization failed.",
      payload: [
        "component",
        "element",
        "error"
      ]
    },
    {
      name:
        "hfds:runtime:component-destroyed",
      owner:
        "HFDS Runtime",
      description:
        "Component destroyed.",
      payload: [
        "component",
        "element"
      ]
    },
    {
      name:
        "hfds:runtime:refreshed",
      owner:
        "HFDS Runtime",
      description:
        "Runtime context refreshed.",
      payload: [
        "context"
      ]
    }
  ].forEach(registerEvent);

  /*
   * Official public APIs.
   *
   * Do not append these APIs to window.HFDS because
   * HFDS Core may be frozen with Object.freeze().
   */
  window.HFDSRuntime =
    publicRuntime;

  window.HFDSEvents =
    publicEvents;

  const start = () => {
    initialize(document);
  };

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      start,
      {
        once: true
      }
    );
  } else {
    start();
  }

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      if (
        event.target instanceof HTMLElement
      ) {
        initialize(event.target);
      }
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      if (
        event.target instanceof HTMLElement
      ) {
        destroy(event.target);
      }
    }
  );

  document.addEventListener(
    "shopify:section:reorder",
    (event) => {
      if (
        event.target instanceof HTMLElement
      ) {
        initialize(event.target);
      }
    }
  );

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      if (
        event.target instanceof HTMLElement
      ) {
        initialize(event.target);
      }
    }
  );
})();